import { GridApiClient} from "../api/grid-api-client";
import {
  SpendingLimitRequest,
  UpdateSpendingLimitRequest,
  UseSpendingLimitRequest,
} from "../types";
import { CreateSpendingLimitResponse, GridError, SpendingLimitResponse, SpendingLimitsResponse, SpendingLimitTransactionResponse } from "../types/common";
import { gridToProgramPeriod } from "../utils/period-mapper";

/**
 * @hidden
 * Service handling spending limit operations
 */
export class SpendingLimitService {
  private apiClient: GridApiClient;

  constructor(apiClient: GridApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Creates a new spending limit.
   *
   * @param smartAccountAddress - The account address to create spending limit for
   * @param request - Spending limit configuration including amount, token, and period
   * @returns Promise resolving to transaction response for spending limit creation
   *
   * @example
   * ```typescript
   * try {
   *   const spendingLimitTx = await gridClient.createSpendingLimit(accountAddress, {
   *     amount: 10000000,
   *     mint: '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU',
   *     period: 'daily',
   *     spendingLimitSigners: ['limit-signer-address']
   *   });
   *   console.log('Spending limit created:', spendingLimitTx.spendingLimitAddress);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to create spending limit:', error.message);
   *   }
   * }
   * ```
   *
   * @category Spending Limits
   */
  async createSpendingLimit(
    smartAccountAddress: string,
    request: SpendingLimitRequest
  ): Promise<CreateSpendingLimitResponse> {
    // Transform period to API format and pass to API client
    // The API client will handle camelCase to snake_case conversion
    const requestPayload: SpendingLimitRequest = {
      ...request,
      period: gridToProgramPeriod(request.period) as any,
    };

    const response = await this.apiClient.createSpendingLimit(
      smartAccountAddress,
      requestPayload
    );

    return response;
  }

  /**
   * Modifies the parameters of an existing spending limit, such as the amount,
   * time period, or associated signers.
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to update
   * @param request - Update request with new spending limit parameters
   * @returns Promise resolving to transaction response for the update
   *
   * @example
   * ```typescript
   * try {
   *   const updateTx = await gridClient.updateSpendingLimit(
   *     accountAddress,
   *     spendingLimitAddress,
   *     {
   *       amount: "5000000",
   *       spendingLimitSigners: ['signer-address']
   *     }
   *   );
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Update failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Spending Limits
   */
  async updateSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string,
    request: UpdateSpendingLimitRequest
  ): Promise<SpendingLimitTransactionResponse> {
    const response = await this.apiClient.updateSpendingLimit(
      accountAddress,
      spendingLimitAddress,
      request
    );

    return response;
  }

  /**
   * Delete a spending limit
   *
   * Removes an existing spending limit from an account, allowing unrestricted
   * spending for the associated token type.
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to delete
   * @returns Promise resolving to transaction response for the deletion
   *
   * @example
   * ```typescript
   * try {
   *   const deleteTx = await gridClient.deleteSpendingLimit(
   *     accountAddress,
   *     spendingLimitAddress
   *   );
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Deletion failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Spending Limits
   */
  async deleteSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string
  ): Promise<SpendingLimitTransactionResponse> {
    const response = await this.apiClient.deleteSpendingLimit(
      accountAddress,
      spendingLimitAddress
    );
    return response;
  }

  /**
   * Use a spending limit for a transaction
   *
   * Utilizes an existing spending limit to authorize a transaction, automatically
   * checking that the transaction amount is within the limit constraints.
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to use
   * @param request - Transaction request to authorize against the spending limit
   * @returns Promise resolving to transaction response for the authorized transaction
   *
   * @example
   * ```typescript
   * try {
   *   const limitTx = await gridClient.useSpendingLimit(
   *     accountAddress,
   *     spendingLimitAddress,
   *     {
   *       amount: 500000000,
   *       signerAddress: "signer-address",
   *       recipientAddress: "recipient-address"
   *     }
   *   );
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Spending limit authorization failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Spending Limits
   */
  async useSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string,
    request: UseSpendingLimitRequest
  ): Promise<SpendingLimitTransactionResponse> {
    const response = await this.apiClient.useSpendingLimit(
      accountAddress,
      spendingLimitAddress,
      request
    );
    return response;
  }

  /**
   * Retrieves a spending limit by its address
   *
   * @param accountAddress - The account address that owns the spending limit
   * @param spendingLimitAddress - The address of the spending limit to retrieve
   * @returns Promise resolving to spending limit data
   *
   * @example
   * ```typescript
   * try {
   *   const spendingLimit = await gridClient.getSpendingLimit(
   *     accountAddress,
   *     spendingLimitAddress
   *   );
   *   console.log('Limit amount:', spendingLimit.amount);
   *   console.log('Remaining:', spendingLimit.remainingAmount);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get spending limit:', error.message);
   *   }
   * }
   * ```
   */
  async getSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string
  ): Promise<SpendingLimitResponse> {
    const response = await this.apiClient.getSpendingLimit(
      accountAddress,
      spendingLimitAddress
    );
    return response;
  }


  /**
   * Retrieves all spending limits for an account
   *
   * @param accountAddress - The account address to get spending limits for
   * @returns Promise resolving to array of spending limit data
   *
   * @example
   * ```typescript
   * try {
   *   const limits = await gridClient.getSpendingLimits(accountAddress);
   *   limits.forEach(limit => {
   *     console.log(`Limit: ${limit.amount}, Remaining: ${limit.remainingAmount}`);
   *   });
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get spending limits:', error.message);
   *   }
   * }
   * ```
   */
  async getSpendingLimits(accountAddress: string): Promise<SpendingLimitsResponse> {
    const response = await this.apiClient.getSpendingLimits(accountAddress);
    return response;
  }
}
